import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useCareCaseService,
  useCareRequestService,
  useFacilitatorStreaming,
  useLeaderStreaming,
  useRegistrationIntakeService,
  useRegistrationStreaming,
  type CareCaseListView,
  type CareRequestListView,
  type FacilitatorModelView,
  type LeaderModelView,
  type RegistrationIntakeListView,
  type RegistrationListView,
} from '@actschurch/ccms-sdk';
import { CASE_STATUS } from '@/pages/Counselling/caseStatusIds';
import { formatSDKDate } from '@/utils/dateTime';
import type { CriticalRow } from '../Components/CriticalListCard';
import type { WorkloadRow } from '../Components/WorkloadTable';
import { CRITICAL_LIST_SIZE, type OverviewDomain } from '../constants';

/**
 * useOverviewLists — the critical lists and the two workload tables.
 *
 * Split from `useOverviewCharts` because these are row lists rather than series, and
 * because several of them are cheap `listView({ take: n })` calls that should not wait
 * on the chart streams.
 *
 * Server-side aggregates (`careCaseService.unassigned`, `leaderService.caseloadStats`,
 * `facilitatorService.loadStats`) are specced in `_deploy/_newEndPoints/` and detected by
 * capability. Until they ship, leader/facilitator load falls back to the streamed
 * `activeCaseCount` / `activeAssignmentsCount` already on the list models, and the
 * unassigned-cases card explains that it is waiting on the endpoint.
 */

export interface UseOverviewListsResult {
  newCases: CriticalRow[];
  newCasesTotal: number;
  staleCases: CriticalRow[];
  staleCasesTotal: number;
  unassignedCases: CriticalRow[];
  unassignedAvailable: boolean;
  pendingRequests: CriticalRow[];
  pendingRequestsTotal: number;
  newIntakes: CriticalRow[];
  newIntakesTotal: number;
  behindScheduleRegistrations: CriticalRow[];
  leaderWorkload: WorkloadRow[];
  facilitatorWorkload: WorkloadRow[];
  loading: boolean;
  refresh: () => void;
}

export function useOverviewLists(domain: OverviewDomain): UseOverviewListsResult {
  const careCaseService = useCareCaseService();
  const careRequestService = useCareRequestService();
  const intakeService = useRegistrationIntakeService();

  const wantsCare = domain === 'care';
  const wantsCourse = domain === 'course';

  const [newCases, setNewCases] = useState<CareCaseListView[]>([]);
  const [newCasesTotal, setNewCasesTotal] = useState(0);
  const [staleCases, setStaleCases] = useState<CareCaseListView[]>([]);
  const [staleCasesTotal, setStaleCasesTotal] = useState(0);
  const [unassigned, setUnassigned] = useState<CareCaseListView[] | null>(null);
  // ⚠️ Typed, not `any[]`. These were `any[]`, and the row mappers below had silently drifted onto
  // fields that do not exist (`r.name`, `r.requesterName`, `i.participantName`), so both cards showed
  // the reference where a person's name belongs and no date at all. `any` is what let that compile.
  const [requests, setRequests] = useState<CareRequestListView[]>([]);
  const [requestsTotal, setRequestsTotal] = useState(0);
  const [intakes, setIntakes] = useState<RegistrationIntakeListView[]>([]);
  const [intakesTotal, setIntakesTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const { items: leaders, load: loadLeaders } = useLeaderStreaming(undefined, 50);
  const { items: facilitators, load: loadFacilitators } = useFacilitatorStreaming(undefined, 50);
  const { items: registrations, load: loadRegistrations } = useRegistrationStreaming(undefined, 50);

  const fetchLists = useCallback(async () => {
    setLoading(true);

    const svc = careCaseService as any;

    const skipped = () => Promise.reject(new Error('not fetched for this domain'));

    const [newRes, staleRes, unassignedRes, requestRes, intakeRes] = await Promise.allSettled([
      wantsCare
        ? careCaseService.listView({
            take: CRITICAL_LIST_SIZE,
            skip: 0,
            statusID: String(CASE_STATUS.New),
          })
        : skipped(),
      wantsCare
        ? careCaseService.listView({ take: CRITICAL_LIST_SIZE, skip: 0, includeStaleOnly: true })
        : skipped(),
      wantsCare && typeof svc.unassigned === 'function'
        ? svc.unassigned(CRITICAL_LIST_SIZE)
        : Promise.reject(new Error('unassigned() not in this SDK version')),
      wantsCare ? careRequestService.listView({ take: CRITICAL_LIST_SIZE, skip: 0 }) : skipped(),
      wantsCourse ? intakeService.listView({ take: CRITICAL_LIST_SIZE, skip: 0 }) : skipped(),
    ]);

    if (newRes.status === 'fulfilled') {
      setNewCases(newRes.value.items ?? []);
      setNewCasesTotal(newRes.value.totalCount ?? 0);
    }

    if (staleRes.status === 'fulfilled') {
      setStaleCases(staleRes.value.items ?? []);
      setStaleCasesTotal(staleRes.value.totalCount ?? 0);
    }

    // Left as null when the SDK has no `unassigned()` — the card renders an explanation
    // rather than an empty list, which would read as "nothing is unassigned".
    setUnassigned(unassignedRes.status === 'fulfilled' ? (unassignedRes.value ?? []) : null);

    if (requestRes.status === 'fulfilled') {
      setRequests(requestRes.value.items ?? []);
      setRequestsTotal(requestRes.value.totalCount ?? 0);
    } else if (wantsCare) {
      console.error('Error fetching care requests:', requestRes.reason);
    }

    if (intakeRes.status === 'fulfilled') {
      setIntakes(intakeRes.value.items ?? []);
      setIntakesTotal(intakeRes.value.totalCount ?? 0);
    } else if (wantsCourse) {
      console.error('Error fetching registration intakes:', intakeRes.reason);
    }

    setLoading(false);
  }, [careCaseService, careRequestService, intakeService, wantsCare, wantsCourse]);

  // Only the streams this page renders are loaded. The other hooks are still called, as the rules
  // of hooks require, but their `load()` is never invoked so they make no requests.
  useEffect(() => { fetchLists(); }, [fetchLists]);
  useEffect(() => { if (wantsCare) loadLeaders(); }, [wantsCare, loadLeaders]);
  useEffect(() => { if (wantsCourse) loadFacilitators(); }, [wantsCourse, loadFacilitators]);
  useEffect(() => { if (wantsCourse) loadRegistrations(); }, [wantsCourse, loadRegistrations]);

  const refresh = useCallback(() => {
    fetchLists();
    if (wantsCare) loadLeaders();
    if (wantsCourse) { loadFacilitators(); loadRegistrations(); }
  }, [fetchLists, loadLeaders, loadFacilitators, loadRegistrations, wantsCare, wantsCourse]);

  // ── Row mapping ───────────────────────────────────────────────────────────

  const caseRow = (c: CareCaseListView): CriticalRow => ({
    id: c.caseID,
    title: c.name ?? `Case #${c.caseID}`,
    meta: [c.needName, c.counseleeNames].filter(Boolean).join(' · ') || undefined,
    badge: c.statusName ?? undefined,
    badgeColor: c.statusColor ?? undefined,
    to: `/Counselling/Cases/${c.caseID}`,
  });

  const staleRow = (c: CareCaseListView): CriticalRow => ({
    ...caseRow(c),
    badge: c.daysSinceLastActivity !== undefined ? `${c.daysSinceLastActivity}d quiet` : undefined,
    badgeColor: c.activityStatusColor ?? undefined,
  });

  // Every derived list is memoised at the top level of the hook — never inside the
  // returned object literal, where the hook order would be easy to break by reordering
  // an object key.
  const pendingRequests = useMemo<CriticalRow[]>(() => requests.map((r) => ({
    id: r.requestID,
    title: r.fullName
      || [r.firstName, r.surname].filter(Boolean).join(' ').trim()
      || r.reference
      || `Request #${r.requestID}`,
    meta: [r.needName, formatSDKDate(r.submittedDateTime)].filter(Boolean).join(' · ') || undefined,
    badge: r.statusName ?? undefined,
    badgeColor: r.statusColor ?? undefined,
    to: `/Counselling/Requests/${r.requestID}`,
  })), [requests]);

  const newIntakes = useMemo<CriticalRow[]>(() => intakes.map((i) => {
    const applicant = i.fullName || [i.firstName, i.surname].filter(Boolean).join(' ').trim();
    const extra = (i.participantCount ?? 0) > 1 ? `+${i.participantCount - 1} more` : '';
    return {
      id: i.intakeID,
      title: applicant || i.reference || `Intake #${i.intakeID}`,
      meta: [i.courseName, extra, formatSDKDate(i.submittedDateTime)]
        .filter(Boolean).join(' · ') || undefined,
      badge: i.statusName ?? undefined,
      badgeColor: i.statusColor ?? undefined,
      to: `/Grow/Intakes/${i.intakeID}`,
    };
  }), [intakes]);

  const behindScheduleRegistrations = useMemo<CriticalRow[]>(
    () => (registrations as RegistrationListView[])
      .filter((r) => r.isBehindSchedule)
      .map((r) => ({
        id: r.registrationID,
        title: r.courseName ?? r.reference ?? `Registration #${r.registrationID}`,
        meta: [r.leadFacilitatorName, r.nextSessionDisplay].filter(Boolean).join(' · ') || undefined,
        badge: `${r.progressPercent ?? 0}%`,
        badgeColor: r.effectiveStatusColor ?? r.statusColor ?? undefined,
        to: `/Grow/Registration/RegistrationDetails/${r.registrationID}`,
      })),
    [registrations],
  );

  const leaderWorkload = useMemo<WorkloadRow[]>(
    () => (leaders as LeaderModelView[]).map((l) => ({
      userId: l.userId,
      fullName: l.fullName ?? `${l.firstName ?? ''} ${l.surname ?? ''}`.trim(),
      avatar: l.avatar,
      primary: l.activeCaseCount ?? 0,
      secondary: l.needsCount ?? 0,
    })),
    [leaders],
  );

  const facilitatorWorkload = useMemo<WorkloadRow[]>(
    () => (facilitators as FacilitatorModelView[]).map((f) => ({
      userId: f.userId,
      fullName: f.fullName ?? `${f.firstName ?? ''} ${f.surname ?? ''}`.trim(),
      avatar: f.avatar,
      primary: f.activeAssignmentsCount ?? 0,
      secondary: f.coursesCount ?? 0,
    })),
    [facilitators],
  );

  return {
    newCases: newCases.map(caseRow),
    newCasesTotal,
    staleCases: staleCases.map(staleRow),
    staleCasesTotal,

    unassignedCases: (unassigned ?? []).map(caseRow),
    unassignedAvailable: unassigned !== null,

    pendingRequests,
    pendingRequestsTotal: requestsTotal,
    newIntakes,
    newIntakesTotal: intakesTotal,
    behindScheduleRegistrations,
    leaderWorkload,
    facilitatorWorkload,

    loading,
    refresh,
  };
}

export default useOverviewLists;
